import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  BookText, Settings, Moon, Sun, Library, LogOut,
  UserCircle, Cloud, ShieldCheck, HardDrive, Info, LogIn, Loader2,
  Menu, X, Sparkles, BookOpen, Headphones, Brain, Mic, Edit3,
  Users, UsersRound, RefreshCw, GripVertical, Plus, Star, Bookmark,
  MoreVertical, Image, Trash2, LayoutGrid, List as ListIcon,
  Book as BookIcon, ChevronLeft, ChevronRight, MessageSquare,
  Volume2, Target, ChevronUp, ChevronDown, AlertCircle, Edit2, Edit, Download, Check, CheckCircle2,
  Search, Eye, Heart
} from 'lucide-react';
import { RatingModal } from '../components/RatingStars';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, ViewMode, Annotation } from '../types';
import { storageService } from '../services/storageService';
import { preloadBookCovers } from '../utils/imagePreload';
import { fileStorageService } from '../services/fileStorageService';
import { dbManager } from '../services/db_manager';
import { FloatingTourButton } from '../components/FloatingTourButton';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { supabase } from '../services/supabaseClient';
import { textToSpeechService } from '../services/textToSpeechService';
import { flashcardManager } from '../services/flashcardManager';
import { notesDeckManager, normalizeLangCode } from '../services/notesDeckManager';
import { getLanguageName } from '../services/languageConstants';
import { translationService } from '../services/translationService';
import { bookService } from '../services/bookService';
import BookDetailsModal from './BookDetailsModal';
import { TopStatsBar } from '../components/TopStatsBar';
import { importRateLimiter } from '../utils/importRateLimit';
import { googleDriveService } from '../services/googleDriveService';
import { tourService } from '../services/tourService';
import { isBookOffline, saveBookOffline, removeBookOffline, getAllOfflineBookIds } from '../services/offlineStorageService';
import UpgradeModal from '../components/UpgradeModal';
import { WatchdogGuard } from '../components/WatchdogGuard';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AppBackButton, goBackOr } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { BookCoverArt } from '../components/BookCoverArt';
import T from '../components/T';
import i18nInstance from '../i18n';


const ITEMS_PER_PAGE = 30;

const LANGUAGE_FLAGS: Record<string, string> = {
  'en': '🇺🇸', 'pt': '🇧🇷', 'fr': '🇫🇷', 'es': '🇪🇸', 'it': '🇮🇹',
  'de': '🇩🇪', 'ru': '🇷🇺', 'ja': '🇯🇵', 'zh': '🇨🇳', 'ar': '🇸🇦'
};

const languageMap: Record<string, string> = {
  'en': 'en-US', 'pt': 'pt-BR', 'fr': 'fr-FR', 'es': 'es-ES', 'it': 'it-IT',
  'de': 'de-DE', 'ru': 'ru-RU', 'ja': 'ja-JP', 'zh': 'zh-CN', 'ar': 'ar-SA'
};

const SkeletonCard: React.FC = () => (
  <div className="w-full aspect-[2/3] bg-slate-200 dark:bg-slate-800 rounded-[32px] animate-pulse flex flex-col p-6 space-y-4">
    <div className="flex-1" />
    <div className="h-4 bg-slate-300 dark:bg-slate-700 rounded-full w-3/4" />
    <div className="h-3 bg-slate-300 dark:bg-slate-700 rounded-full w-1/2" />
  </div>
);

const BookCover: React.FC<{ book: { title: string, author?: string, language?: string, coverUrl?: string, format?: 'pdf' | 'html' }, isSmall?: boolean }> = ({ book, isSmall = false }) => {
  const [hasError, setHasError] = useState(false);
  const displayCover = book.coverUrl;
  const isGenerated = !displayCover || displayCover.startsWith('generated://');
  // Design 2.0: imagem com lombada ou capa gerada em tom pastel (título fica abaixo, no cartão)
  return (
    <BookCoverArt
      src={!isGenerated && !hasError ? displayCover : undefined}
      title={book.title}
      author={book.author || 'Autor Desconhecido'}
      isSmall={isSmall}
      onError={() => setHasError(true)}
    />
  );
};

const NotesListModal: React.FC<{ book: Book; onClose: () => void; }> = ({ book, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { targetLanguage } = useTheme();
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [activeNoteActionId, setActiveNoteActionId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<Annotation | null>(null);
  const [editContent, setEditContent] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const { user } = useAuth();

  const refreshNotes = async () => {
    if (user && !user.isGuest) {
      const response = await translationService.getBookTranslations(user.id, book.id);
      if (response.data) {
        setAnnotations(response.data.map((a: any) => ({
          ...a,
          bookId: a.book_id,
          pageNumber: a.page_number,
          createdAt: new Date(a.created_at).getTime()
        })) as Annotation[]);
        return;
      }
    }
    setAnnotations(storageService.getAnnotations(book.id));
  };

  useEffect(() => { refreshNotes(); }, [book.id]);

  const parseNote = (content: string) => {
    const parts = content.split(' -> ');
    return { original: parts[0] || '', translation: parts[1] || '' };
  };

  const handleGoToPage = async (page: number, textToHighlight: string) => {
    if (user && !user.isGuest) {
      await bookService.updateBookFields(user.id, book.id, { last_page: page });
    } else {
      const total = book.totalPages || 100;
      const percent = Math.round((page / total) * 100);
      localStorage.setItem(`progresso_${book.id}`, JSON.stringify({ page, percent }));
    }
    navigate(`/reader/${book.id || book.custom_id}`, { state: { focusSentence: textToHighlight } });
    onClose();
  };

  const handleSaveToAllPracticeModes = async (a: Annotation) => {
    const { original, translation } = parseNote(a.content);
    const rawLang = a.detectedLocale || book.language || 'en';
    const locale = normalizeLangCode(rawLang);
    const category = getLanguageName(rawLang);

    flashcardManager.saveFlashcard(original, translation, locale, undefined, category);
    notesDeckManager.addToPronunciation(original, translation, locale, category);
    notesDeckManager.addToWriting(original, translation, locale, category);

    if (user && !user.isGuest) {
      try {
        await flashcardManager.saveFlashcard(original, translation, locale, book.id, category);
      } catch (err) {
        console.error("Cloud sync failed (Home D1):", err);
      }
    }

    alert('✅ Salvo em Flashcards, Pronúncia e Escrita!');
    setActiveNoteActionId(null);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md" onClick={onClose}>
      <div className="bg-surface w-full max-w-2xl rounded-[28px] overflow-hidden flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
        <div className="p-8 border-b border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 text-primary rounded-2xl"><MessageSquare size={24} /></div>
            <div>
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('library.notes.title')}</h3>
              <p className="text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em] truncate max-w-[300px]">{book.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={24} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-6">
          {annotations.length === 0 ? (
            <div className="py-20 text-center flex flex-col items-center gap-4 text-slate-400 font-bold">
              <BookText size={32} />
              <p>{t('library.notes.empty')}</p>
            </div>
          ) : (
            annotations.map(anno => {
              const { original, translation } = parseNote(anno.content);
              const isActive = activeNoteActionId === anno.id;
              return (
                <div key={anno.id} className="space-y-2">
                  <div onClick={() => setActiveNoteActionId(isActive ? null : anno.id)} className={`p-6 rounded-3xl border transition-all cursor-pointer group ${isActive ? 'bg-primary/5 border-primary' : 'bg-slate-50 dark:bg-slate-800/50 border-black/[0.06] dark:border-white/[0.06]'}`}>
                    <p className="text-[14px] font-semibold text-primary mb-3">"{original}"</p>
                    <p className="text-[13px] font-medium text-slate-700 dark:text-slate-300">{translation}</p>
                    <div className="mt-4 flex items-center justify-between text-[8px] font-normal uppercase tracking-[0.14em] font-mono text-slate-400">
                       <span>{new Date(anno.createdAt).toLocaleDateString()}</span>
                       <span className="bg-primary/10 text-primary px-2 py-0.5 rounded">Pág. {anno.pageNumber}</span>
                    </div>
                  </div>
                  {isActive && (
                    <div className="flex items-center justify-around bg-slate-100 dark:bg-slate-800 p-2 rounded-2xl border border-black/[0.06] dark:border-slate-700 mx-4">
                      <button onClick={() => textToSpeechService.speak(original, anno.detectedLocale || book.language || targetLanguage)} className="p-3 text-ok"><Volume2 size={20} /></button>
                      <button onClick={() => handleGoToPage(anno.pageNumber, original)} className="p-3 text-tintd-sky"><Target size={20} /></button>
                      <button onClick={() => handleSaveToAllPracticeModes(anno)} className="p-3 text-tintd-lavender"><Brain size={20} /></button>
                      <button onClick={() => { setEditingNote(anno); setEditContent(translation); }} className="p-3 text-warn"><Edit size={20} /></button>
                      <button onClick={() => setShowDeleteConfirm(anno.id)} className="p-3 text-bad"><Trash2 size={20} /></button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="p-8 bg-slate-50 dark:bg-slate-900/50 border-t border-black/[0.06] shrink-0">
          <button onClick={onClose} className="text-[13px] font-medium w-full h-12 bg-surface dark:bg-slate-800 border border-black/[0.06] rounded-full">{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal: React.FC<{ isOpen: boolean; title: string; message: string; onConfirm: () => void; onCancel: () => void; confirmLabel?: string; isDanger?: boolean; hideCancel?: boolean; }> = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel = 'Confirmar', isDanger = false, hideCancel = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm">
      <div className="bg-surface p-8 rounded-[28px] w-full max-w-md animate-in zoom-in-95">
        <div className={`w-12 h-12 rounded-2xl mb-6 flex items-center justify-center ${isDanger ? 'bg-tint-rose text-bad' : 'bg-primary/10 text-primary'}`}><AlertCircle size={24} /></div>
        <h3 className="text-lg font-semibold dark:text-ink mb-2">{title}</h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-8">{message}</p>
        <div className={hideCancel ? "grid grid-cols-1 gap-3" : "grid grid-cols-2 gap-3"}>
          {!hideCancel && <button onClick={onCancel} className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full">Cancelar</button>}
          <button onClick={onConfirm} className={`py-4 ${isDanger ? 'bg-bad' : 'bg-primary'} text-white rounded-2xl font-normal text-[10px] uppercase tracking-[0.14em] font-mono`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const InputModal: React.FC<{ isOpen: boolean; title: string; placeholder: string; initialValue: string; onSave: (value: string) => void; onCancel: () => void; }> = ({ isOpen, title, placeholder, initialValue, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { 
    if (isOpen) { 
      setValue(initialValue); 
      const timer = setTimeout(() => inputRef.current?.focus(), 100); 
      return () => clearTimeout(timer);
    } 
  }, [isOpen, initialValue]);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm">
      <div className="bg-surface p-8 rounded-[28px] w-full max-w-lg animate-in zoom-in-95">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary mb-6 flex items-center justify-center"><Edit2 size={24} /></div>
        <h3 className="text-lg font-semibold dark:text-ink mb-6">{title}</h3>
        <input ref={inputRef} type="text" value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-2xl px-6 py-4 mb-8 font-bold dark:text-ink outline-none ring-2 ring-transparent focus:ring-primary/30" />
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onCancel} className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full">{t('home.cancelBtn', 'Cancelar')}</button>
          <button onClick={() => onSave(value)} className="text-[13px] font-medium h-12 bg-primary text-slate-900 rounded-full">{t('common.save', 'Salvar')}</button>
        </div>
      </div>
    </div>
  );
};

const HomePage: React.FC<{ isShadowingView?: boolean }> = ({ isShadowingView = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDarkMode, toggleAppOnlyTheme, accentColor } = useTheme();
  const { user } = useAuth();
  const { books, profile, loading: dataLoading, refreshBooks, setBooks, isSyncing } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [shelfFilter, setShelfFilter] = useState<'recent' | 'last_read' | 'favorites' | 'reading' | 'unread' | 'completed'>('recent');
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('viewMode') as ViewMode) || 'grid');
  const [activeBookId, setActiveBookId] = useState<string | null>(null);
  const [selectedBookForModal, setSelectedBookForModal] = useState<Book | null>(null);
  const [showNotesForBook, setShowNotesForBook] = useState<Book | null>(null);
  const [activeTab, setActiveTab] = useState<'continue' | 'favorites' | 'catalog' | 'stats' | 'offline'>('continue');
  const [offlineBookIds, setOfflineBookIds] = useState<Set<string>>(new Set());
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [downloadingBookId, setDownloadingBookId] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  useEffect(() => {
    getAllOfflineBookIds().then(ids => setOfflineBookIds(new Set(ids)));
  }, []);

  const confirmDownloadOffline = (bookId: string) => {
    setBookToDownload(bookId);
    setModalType('download_confirm');
  };

  const executeRemoveDownloadOffline = async (bookId: string) => {
      try {
        const { removeBookOffline } = await import('../services/offlineStorageService');
        await removeBookOffline(bookId);
        setOfflineBookIds(prev => {
          const next = new Set(prev);
          next.delete(bookId);
          return next;
        });
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        if (token) {
           await fetch(`/api/books/remove?bookId=${bookId}`, {
             method: 'POST',
             headers: { 'Authorization': `Bearer ${token}` }
           });
        }
      } catch (err) {
        console.error('Failed to remove offline book', err);
      }
  };

  const executeDownloadOffline = async () => {
    if (!bookToDownload) return;
    const bookId = bookToDownload;
    setModalType('none');
    setBookToDownload(null);
    if (offlineBookIds.has(bookId)) {
      await executeRemoveDownloadOffline(bookId);
      return;
    }

    // Download to offline
    setDownloadingBookId(bookId);
    try {
      if (!user || user.isGuest) throw new Error('Must be logged in');
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('No auth token');

      const res = await fetch(`/api/books/download?bookId=${bookId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (!res.ok) {
        if (data.requireUpgrade) {
          setIsUpgradeModalOpen(true);
          return;
        }
        throw new Error(data.error || 'Failed to download');
      }

      await saveBookOffline(bookId, data.payload);
      setOfflineBookIds(prev => new Set(prev).add(bookId));
    } catch (err) {
      console.error('Download failed', err);
      alert(t('home.downloadFailed', 'Falha ao baixar o livro para leitura offline.'));
    } finally {
      setDownloadingBookId(null);
    }
  };
  const [modalType, setModalType] = useState<'none' | 'rename' | 'delete' | 'cover' | 'bulk_delete' | 'upgrade' | 'limit_reached' | 'delete_success' | 'download_confirm'>('none');
  const [currentPage, setCurrentPage] = useState(1);
  const [bookToDownload, setBookToDownload] = useState<string | null>(null);
  // Optimistic favorites: maps book.id → boolean override for instant UI feedback
  const [optimisticFavorites, setOptimisticFavorites] = useState<Record<string, boolean>>({});
  const [isManualSyncing, setIsManualSyncing] = useState(false);

  useEffect(() => {
    if (books && books.length > 0) {
      preloadBookCovers(books, 20);
      
      // Auto-enrich first page books with metadata if missing (one-time check)
      const enrichBooks = async () => {
        try {
          const { cacheManager } = await import('../services/cacheManager');
          let libraryData: any[] = [];
          
          const cached = await cacheManager.getAsync<any>('online_library_metadata_v2');
          if (cached) {
            const parsed = cached.data || cached;
            libraryData = Array.isArray(parsed) ? parsed : [];
          } else {
            const response = await fetch('https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json');
            if (response.ok) {
              const json = await response.json();
              libraryData = Array.isArray(json) ? json : json.data || [];
              if (libraryData.length > 0) {
                cacheManager.set('online_library_metadata_v2', libraryData);
              }
            }
          }

          if (libraryData.length === 0) return;

          const firstPageBooks = books.slice(0, ITEMS_PER_PAGE);
          let hasChanges = false;
          
          for (const book of firstPageBooks) {
            if (!book.description) {
              let extractedId = null;
              if (book.fileUrl && book.fileUrl.match(/\/(\d+)\.(html|pdf)$/i)) {
                extractedId = book.fileUrl.match(/\/(\d+)\.(html|pdf)$/i)![1];
              } else if (book.coverUrl && book.coverUrl.match(/\/(\d+)\.jpg$/i)) {
                extractedId = book.coverUrl.match(/\/(\d+)\.jpg$/i)![1];
              } else if (String(book.id).startsWith('gut-')) {
                const parts = String(book.id).split('-');
                if (parts.length >= 2) extractedId = parts[1];
              } else if (String(book.custom_id).startsWith('gut-')) {
                const parts = String(book.custom_id).split('-');
                if (parts.length >= 2) extractedId = parts[1];
              }
              
              const bookIdToMatch = extractedId || String(book.custom_id || book.id);
              const meta = libraryData.find((item: any) => 
                String(item.id || item.gutenberg_id) === String(bookIdToMatch) || 
                (item.title && book.title && item.title.trim().toLowerCase() === book.title.trim().toLowerCase())
              );
              if (meta) {
                const newDesc = meta.description || meta.descricao || "";
                if (newDesc) {
                  await storageService.saveBook({
                    ...book,
                    description: newDesc,
                    reading_level: meta.reading_level || book.reading_level,
                    subjects: meta.subject || meta.subjects || book.subjects
                  });
                  hasChanges = true;
                }
              }
            }
          }
          if (hasChanges) {
             console.log("[Home] Metadata enriched for shelf books.");
             refreshBooks();
          }
        } catch (e) {
          console.error("Enrichment failed", e);
        }
      };
      
      enrichBooks();
    }
  }, [books.length]); // Only run when total count changes, not on every content update

  useEffect(() => {
    refreshBooks();
  }, [refreshBooks]);



  useEffect(() => {
    if (typeof googleDriveService !== 'undefined') {
      googleDriveService.loadGapiAndGis().catch(err => {
        console.warn('[HomePage] Falha ao carregar SDK do Google:', err);
      });
    }
  }, []);

  const handleManualSync = async () => {
    setIsManualSyncing(true);
    try {
      // 1. Sincronizar/atualizar livros do nosso D1 bucket
      await refreshBooks(true);
      
      // 2. Se o Google Drive estiver conectado, sincronizar dele também
      let isDriveConnected = false;
      try {
        if (typeof googleDriveService !== 'undefined') {
          isDriveConnected = !!googleDriveService.getToken() || !!googleDriveService.getUserInfo();
        }
      } catch (e) {}

      if (isDriveConnected && typeof googleDriveService !== 'undefined') {
        // Remove a flag temporariamente para garantir sincronização forçada
        sessionStorage.removeItem('libritui_drive_synced');
        console.log('[HomePage] Sincronizando livros do Google Drive manualmente...');
        const result = await googleDriveService.syncFromDrive();
        sessionStorage.setItem('libritui_drive_synced', 'true');
        if (result.importedCount > 0) {
          await refreshBooks(true);
        }
      }
    } catch (err) {
      console.error('Erro na sincronização manual:', err);
    } finally {
      setIsManualSyncing(false);
    }
  };

  // Google Drive BYOS auto-sync rehydration
  useEffect(() => {
    if (!user?.id) return;

    let isDriveConnected = false;
    try {
      if (typeof googleDriveService !== 'undefined') {
        isDriveConnected = !!googleDriveService.getToken() || !!googleDriveService.getUserInfo();
      }
    } catch (e) {
      console.warn('googleDriveService is not defined yet', e);
    }

    if (!isDriveConnected) return;

    const hasSyncedInSession = sessionStorage.getItem('libritui_drive_synced') === 'true';
    if (hasSyncedInSession) return;

    console.log('[BYOS] Iniciando reidratação/sincronização do Google Drive...');
    googleDriveService.syncFromDrive()
      .then(async (result) => {
        console.log('[BYOS] Sincronização concluída.', result);
        sessionStorage.setItem('libritui_drive_synced', 'true');
        if (result.importedCount > 0) {
          console.log(`[BYOS] ${result.importedCount} novos livros importados da nuvem. Atualizando estante.`);
          await refreshBooks(true);
        }
      })
      .catch((err) => {
        console.error('[BYOS] Erro na sincronização automática:', err);
      });
  }, [user?.id, refreshBooks]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isFree = profile?.plan_type === 'free';
    if (isFree && books.length >= 3) {
      setModalType('limit_reached');
      return;
    }

    try {
      // 1. Rate Limiting
      importRateLimiter.checkLimit();

      // 2. File Validation
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const allowedExts = ['pdf', 'html', 'htm', 'epub'];
      
      if (!allowedExts.includes(extension)) {
         alert("Formato de arquivo não suportado. Use PDF, HTML ou EPUB.");
         return;
      }

      const MAX_SIZE = 50 * 1024 * 1024; // 50MB
      if (file.size > MAX_SIZE) {
        alert("O arquivo é muito grande. O limite máximo é de 50MB.");
        return;
      }

      setIsUploading(true);

      const bookId = user?.isGuest ? Math.random().toString(36).substr(2, 9) : crypto.randomUUID();
      const format = (extension === 'html' || extension === 'htm') ? 'html' : (extension === 'epub' ? 'epub' : 'pdf');
      const title = file.name.replace(/\.(pdf|html?|epub)$/i, '');
      const author = format === 'pdf' ? 'Documento PDF' : (format === 'epub' ? 'Livro EPUB' : 'Documento HTML');
      const coverUrl = `generated://${bookId}`;
      let fileUrl = 'indexeddb://' + bookId;

      // 1. Salva o arquivo fisicamente no cache (IndexedDB) para TODOS os usuários
      await fileStorageService.saveFile(bookId, file);
      
      // 2. Define o MIME type correto, com fallback específico para EPUB
      const mimeType = file.type || (
        extension === 'epub' ? 'application/epub+zip' :
        (extension === 'html' || extension === 'htm') ? 'text/html' : 
        'application/pdf'
      );
      localStorage.setItem(`mime_${bookId}`, mimeType);

      // 3. Salva os metadados APENAS no cache local (LocalStorage)
      // NUNCA sobe nada para o D1 ou R2, independentemente de estar logado
      storageService.saveBook({
        id: bookId,
        title,
        author,
        coverUrl,
        fileUrl,
        lastPage: 1,
        totalPages: 0,
        dateAdded: Date.now(),
        category: format === 'pdf' ? 'Meus PDFs' : (format === 'epub' ? 'Meus EPUBs' : 'Meus HTMLs'),
        isFavorite: false,
        order: 0,
        language: 'pt',
        format
      });
      await refreshBooks(true);
      
      // Backup assíncrono e não-bloqueante no Google Drive (BYOS) se o token estiver ativo
      let driveToken = null;
      try {
        if (typeof googleDriveService !== 'undefined') {
          driveToken = googleDriveService.getToken();
        }
      } catch (e) {
        console.warn('googleDriveService is not defined yet in this bundle version', e);
      }

      // Abre o livro automaticamente após importar (Prioridade 1 - UI/Local)
      navigate('/reader/' + bookId, { state: { showImportModal: true, isDriveLinked: !!driveToken } });

      // Dispara o upload do Google Drive isolado e sem bloquear a thread
      if (driveToken && typeof googleDriveService !== 'undefined') {
        const fileClone = new Blob([file], { type: mimeType });
        const fileName = file.name;
        
        console.log('[BYOS] Iniciando backup em segundo plano do arquivo:', fileName);
        googleDriveService.uploadFile(fileName, fileClone, mimeType, bookId)
          .then(() => console.log('[BYOS] Backup em segundo plano concluído para:', fileName))
          .catch(driveErr => console.error('[BYOS] Erro ao salvar backup em segundo plano:', driveErr));
      }
    } catch (err: any) {
      console.error("Erro no processo de upload:", err);
      alert("Erro ao salvar livro: " + (err.message || "Erro desconhecido"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleToggleFavorite = async (id: string) => {
    const book = books.find(b => b.id === id || b.custom_id === id);
    if (!book) return;
    const currentFav = id in optimisticFavorites ? optimisticFavorites[id] : !!book.isFavorite;
    const nextFavorite = !currentFav;

    // 1. Optimistic UI update immediately
    setOptimisticFavorites(prev => ({ ...prev, [id]: nextFavorite }));

    // 2. Always update local storage
    storageService.saveBook({ ...book, isFavorite: nextFavorite });

    if (user) {
      try {
        const matchFilter = book.id.includes(':') ? { id: book.id } : { custom_id: book.id };
        await dbManager.from('books', user.id).update({ is_favorite: nextFavorite ? 1 : 0 }, matchFilter);
        // After server confirms, we can clear optimistic if we want, but keeping it alive until refresh is safer
        await refreshBooks(true);
        // Delay slightly before clearing optimistic to let D1 propagate through DataContext
        setTimeout(() => {
          setOptimisticFavorites(prev => { const n = { ...prev }; delete n[id]; return n; });
        }, 1500);
      } catch (err) {
        console.error("Erro ao favoritar livro no D1:", err);
        setOptimisticFavorites(prev => { const n = { ...prev }; delete n[id]; return n; });
      }
    } else {
      refreshBooks();
    }
  };

  const handleToggleCompleted = async (id: string) => {
    const book = books.find(b => b.id === id || b.custom_id === id);
    if (!book) return;
    const isComp = book.status === 'completed' || !!book.isCompleted;
    const nextCompleted = !isComp;

    const updatedBook = {
      ...book,
      status: nextCompleted ? 'completed' : 'reading',
      isCompleted: nextCompleted
    };

    storageService.saveBook(updatedBook);

    if (user) {
      try {
        const matchFilter = book.id.includes(':') ? { id: book.id } : { custom_id: book.id };
        await dbManager.from('books', user.id).update({
          status: nextCompleted ? 'completed' : 'reading',
          is_completed: nextCompleted ? 1 : 0
        }, matchFilter);
        await refreshBooks(true);
      } catch (err) {
        console.error("Failed to toggle completion in D1:", err);
      }
    } else {
      refreshBooks();
    }
  };

  const executeDelete = async () => {
    if (!activeBookId) return;
    const idToRemove = activeBookId;
    await deleteSingleBook(idToRemove); 
    setModalType('delete_success');
  };

  const deleteSingleBook = async (id: string) => {
    const book = books.find(b => b.id === id || b.custom_id === id);
    if (user && !user.isGuest && user.id) {
      try {
        if (book) {
          // Delete by its absolute ID in D1 to be safe
          await dbManager.from('books', user.id).deleteMatch({ id: book.id });
        } else {
          // Fallback for cases where book is not in state
          const isNumeric = /^\d+$/.test(id);
          if (isNumeric) {
            await dbManager.from('books', user.id).deleteMatch({ id });
          } else {
            await dbManager.from('books', user.id).deleteMatch({ custom_id: id });
          }
        }
      } catch (error: any) {
        console.error('[Delete] Falha ao remover do D1:', error.message);
      }
    }
    
    // Explicitly delete from local storage and refresh to update UI immediately
    storageService.deleteBook(id);
    await fileStorageService.deleteFile(id);
    
    // Reactive UI Update: instantly remove from state
    setBooks(prev => prev.filter(b => b.id !== id));
    
    // Re-fetch to confirm sync state
    await refreshBooks(true);
  };

  const handleBatchDelete = async () => {
    const ids: string[] = Array.from(selectedIds);
    setSelectedIds(new Set());
    setIsSelectionMode(false);
    await Promise.all(ids.map(id => deleteSingleBook(id)));
    setModalType('delete_success');
  };

  const handleOpenBook = async (book: Book) => {
    setSelectedBookForModal(book);
    
    if (!book.description) {
      try {
        const { cacheManager } = await import('../services/cacheManager');
        let libraryData: any[] = [];
        
        const cached = await cacheManager.getAsync<any>('online_library_metadata_v2');
        if (cached) {
          const parsed = cached.data || cached;
          libraryData = Array.isArray(parsed) ? parsed : [];
        } else {
          const response = await fetch('https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json');
          if (response.ok) {
            const json = await response.json();
            libraryData = Array.isArray(json) ? json : json.data || [];
            if (libraryData.length > 0) {
              cacheManager.set('online_library_metadata_v2', libraryData);
            }
          }
        }

        if (libraryData && libraryData.length > 0) {
          let extractedId = null;
          if (book.fileUrl && book.fileUrl.match(/\/(\d+)\.(html|pdf)$/i)) {
            extractedId = book.fileUrl.match(/\/(\d+)\.(html|pdf)$/i)![1];
          } else if (book.coverUrl && book.coverUrl.match(/\/(\d+)\.jpg$/i)) {
            extractedId = book.coverUrl.match(/\/(\d+)\.jpg$/i)![1];
          } else if (String(book.id).startsWith('gut-')) {
            const parts = String(book.id).split('-');
            if (parts.length >= 2) extractedId = parts[1];
          } else if (String(book.custom_id).startsWith('gut-')) {
            const parts = String(book.custom_id).split('-');
            if (parts.length >= 2) extractedId = parts[1];
          }
          
          const bookIdToMatch = extractedId || String(book.custom_id || book.id);
          const meta = libraryData.find((item: any) => 
            String(item.id || item.gutenberg_id) === String(bookIdToMatch) || 
            (item.title && book.title && item.title.trim().toLowerCase() === book.title.trim().toLowerCase())
          );
          
          if (meta) {
            const newDesc = meta.description || meta.descricao || "";
            if (newDesc) {
              const enrichedBook = {
                ...book,
                description: newDesc,
                reading_level: meta.reading_level || book.reading_level,
                subjects: meta.subject || meta.subjects || book.subjects,
                language: (meta.language || meta.idioma) ? String(meta.language || meta.idioma).split(/[;,\\s]/)[0].toLowerCase() : book.language
              };
              setSelectedBookForModal(enrichedBook);
              await storageService.saveBook(enrichedBook);
              refreshBooks();
            }
          }
        }
      } catch (e) {
        console.error("Failed to enrich on open", e);
      }
    }
  };

  const handleMoveBook = async (id: string, delta: number) => {
    const index = books.findIndex(b => b.id === id || b.custom_id === id);
    if (index === -1) return;
    const targetIndex = index + delta;
    if (targetIndex < 0 || targetIndex >= books.length) return;

    const newBooks = [...books];
    const [movedBook] = newBooks.splice(index, 1);
    newBooks.splice(targetIndex, 0, movedBook);

    storageService.updateBooksBatch(newBooks);

    if (user && !user.isGuest) {
      for (let i = 0; i < newBooks.length; i++) {
        const b = newBooks[i];
        const matchFilter = b.id.includes(':') ? { id: b.id } : { custom_id: b.id };
        await dbManager.from('books', user.id).update({ display_order: i }, matchFilter);
      }
    }
    refreshBooks();
  };

  const handleToggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredAndSortedBooks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAndSortedBooks.map(b => b.id)));
    }
  };

  const executeRename = async (newName: string) => {
    if (!activeBookId || !newName.trim()) return;
    if (user && !user.isGuest) {
      const isNumeric = /^\d+$/.test(activeBookId);
      if (isNumeric) {
        await dbManager.from('books', user.id).update({ title: newName.trim() }, { id: activeBookId });
      } else {
        await dbManager.from('books', user.id).update({ title: newName.trim() }, { custom_id: activeBookId });
      }
    }
    refreshBooks();
    setModalType('none');
  };

  const executeChangeCover = async (newUrl: string) => {
    if (!activeBookId || !newUrl.trim()) return;
    if (user && !user.isGuest) {
      const isNumeric = /^\d+$/.test(activeBookId);
      if (isNumeric) {
        await dbManager.from('books', user.id).update({ cover_url: newUrl.trim() }, { id: activeBookId });
      } else {
        await dbManager.from('books', user.id).update({ cover_url: newUrl.trim() }, { custom_id: activeBookId });
      }
    }
    refreshBooks();
    setModalType('none');
  };

  // Merge optimistic favorites so star color changes instantly on click
  const booksWithOptimistic = books.map(b =>
    b.id in optimisticFavorites ? { ...b, isFavorite: optimisticFavorites[b.id] } : b
  );

  // Filter and Sort Logic
  const filteredAndSortedBooks = useMemo(() => {
    let result = [...booksWithOptimistic];
    if (searchQuery) {
        result = result.filter(b => b.title.toLowerCase().includes(searchQuery.toLowerCase()) || (b.author && b.author.toLowerCase().includes(searchQuery.toLowerCase())));
    }

    // Sorting
    if (shelfFilter === 'recent') {
      result.sort((a, b) => (b.dateAdded || 0) - (a.dateAdded || 0));
    } else if (shelfFilter === 'last_read') {
      // In this app, we can use progress percentage or a last_read timestamp if available
      // Since lastPage/totalPages determines progress, we can sort by that as a proxy or use localStorage timestamps
      result.sort((a, b) => {
        const progA = localStorage.getItem(`progresso_${a.id}`);
        const progB = localStorage.getItem(`progresso_${b.id}`);
        const timeA = progA ? JSON.parse(progA).timestamp || 0 : 0;
        const timeB = progB ? JSON.parse(progB).timestamp || 0 : 0;
        return timeB - timeA;
      });
    }

    // Filtering
    if (shelfFilter === 'favorites') {
      result = result.filter(b => b.isFavorite);
    } else if (shelfFilter === 'reading') {
      result = result.filter(b => {
        const prog = b.progress !== undefined ? b.progress : (
           localStorage.getItem(`progresso_${b.id}`) ? JSON.parse(localStorage.getItem(`progresso_${b.id}`)!).percent : 0
        );
        return prog > 0 && prog < 100;
      });
    } else if (shelfFilter === 'unread') {
      result = result.filter(b => {
        const prog = b.progress !== undefined ? b.progress : (
           localStorage.getItem(`progresso_${b.id}`) ? JSON.parse(localStorage.getItem(`progresso_${b.id}`)!).percent : 0
        );
        return prog === 0;
      });
    } else if (shelfFilter === 'completed') {
      result = result.filter(b => {
        const prog = b.progress !== undefined ? b.progress : (
           localStorage.getItem(`progresso_${b.id}`) ? JSON.parse(localStorage.getItem(`progresso_${b.id}`)!).percent : 0
        );
        return prog === 100;
      });
    }

    return result;
  }, [booksWithOptimistic, shelfFilter, searchQuery]);

  const totalPages = Math.ceil(filteredAndSortedBooks.length / ITEMS_PER_PAGE);
  const currentBooks = filteredAndSortedBooks.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const getFilterLabel = () => {
    switch(shelfFilter) {
      case 'recent': return t('library.filter.recent', 'Adicionados recentes');
      case 'last_read': return t('library.filter.lastRead', 'Acessado por último');
      case 'favorites': return t('library.filter.favorites', 'Favoritos');
      case 'reading': return t('library.filter.reading', 'Lendo');
      case 'unread': return t('library.filter.unread', 'Não lidos');
      case 'completed': return t('library.filter.completed', 'Leitura completa');
      default: return t('library.filter.organizeBy', 'Filtros');
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 px-4 md:px-0">
      {isShadowingView ? (
        <AppBackButton to="/study-hub" className="mb-2" />
      ) : (
        <AppBackButton
          id="tour-library-btn"
          className="mb-2"
          onClick={() => {
             if (tourService.isActive() && tourService.getPhase() === 'LIBRARY') {
                 tourService.stopTour();
             }
             goBackOr(navigate, '/library-hub');
          }}
        />
      )}

      <div className="flex flex-col gap-6">
        {isShadowingView ? (
          <div className="space-y-4">
            <StudyPageHeader
              tourKey="shadowing"
              title="Shadowing"
              actions={isSyncing ? (
                <span className="flex items-center gap-1.5 h-8 px-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted bg-surface rounded-full">
                  <Loader2 size={11} className="animate-spin" />
                  Sync
                </span>
              ) : undefined}
            />
          </div>
        ) : (
          <StudyPageHeader
            tourKey="library"
            title={t('library.title')}
            actions={
              <>
                {isSyncing && (
                  <span className="flex items-center gap-1.5 h-8 px-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted bg-surface rounded-full">
                    <Loader2 size={11} className="animate-spin" />
                    Sync
                  </span>
                )}
                <span data-tour="library-links" className="flex items-center gap-2">
                  <Link to="/audiobooks" className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone flex items-center justify-center transition-colors" title="Audiobooks">
                    <AnimatedIcon motion="bounce"><Headphones size={17} strokeWidth={1.75} /></AnimatedIcon>
                  </Link>
                  <Link to="/libritui-library" className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone flex items-center justify-center transition-colors" title="Biblioteca LibriTui">
                    <AnimatedIcon motion="flip"><BookOpen size={17} strokeWidth={1.75} /></AnimatedIcon>
                  </Link>
                </span>
              </>
            }
          />
        )}

        <div className="flex flex-wrap items-center gap-3">
          {isSelectionMode ? (
            <div className="flex items-center gap-2 w-full justify-between sm:justify-start">
              <button
                onClick={handleSelectAll}
                className="px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-medium text-[10px] hover:bg-slate-200 transition-all flex-shrink-0"
              >
                {selectedIds.size === filteredAndSortedBooks.length ? t('home.unselectAllBtn', 'DESMARCAR') : t('home.selectAllBtn', 'TUDO')}
              </button>
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setModalType('bulk_delete')}
                  className="p-3 bg-tint-rose text-bad rounded-2xl hover:bg-tint-rose transition-all animate-in zoom-in-90 flex-shrink-0"
                >
                  <Trash2 size={20} />
                </button>
              )}
              <button
                onClick={() => { setIsSelectionMode(false); setSelectedIds(new Set()); }}
                className="px-6 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[10px] flex-shrink-0 ml-auto sm:ml-0"
              >
                {t('home.doneBtn', 'CONCLUIR')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 w-full justify-between sm:justify-start relative">
              <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                <button
                  id="tour-search-btn"
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className={`w-11 h-11 flex items-center justify-center rounded-full border transition-all flex-shrink-0 ${isSearchOpen ? 'bg-ink text-surface border-ink' : 'bg-surface dark:bg-slate-800 border-black/[0.06] dark:border-slate-700 text-slate-600 hover:text-primary'}`}
                  title="Pesquisar"
                >
                  <AnimatedIcon motion="wobble"><Search size={18} strokeWidth={1.75} /></AnimatedIcon>
                </button>

                {isSearchOpen && (
                  <div className="flex-1 sm:w-80 animate-in fade-in slide-in-from-left-2 duration-200">
                    <input
                      id="tour-search-bar"
                      type="text"
                      placeholder={t('library.searchPlaceholder', 'Pesquise pelo seu livro aqui...')}
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      className="w-full h-11 bg-tint-stone border-0 rounded-full px-5 text-sm text-ink placeholder:text-muted focus:ring-2 focus:ring-ink/10"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* 3-dots Menu Button */}
              <div className="relative">
                <button
                  id="tour-more-btn"
                  onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                  className={`w-11 h-11 flex items-center justify-center rounded-full border transition-all flex-shrink-0 ${isMoreMenuOpen ? 'bg-ink text-surface border-ink' : 'bg-surface dark:bg-slate-800 border-black/[0.06] dark:border-slate-700 text-slate-600 hover:text-primary'}`}
                  title={i18nInstance.t('ui.common.options')}
                >
                  <MoreVertical size={18} strokeWidth={1.75} />
                </button>

                {isMoreMenuOpen && (
                  <div className={`absolute top-full ${isSearchOpen ? 'right-0' : 'left-0'} mt-2 w-64 max-w-[calc(100vw-2rem)] bg-surface rounded-[24px] border border-black/[0.06] dark:border-white/[0.06] p-2 z-[100] animate-in slide-in-from-top-2 duration-300`}>
                    <button
                      onClick={() => { handleManualSync(); setIsMoreMenuOpen(false); }}
                      className="w-full flex items-center gap-3 p-3.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-all"
                    >
                      <RefreshCw size={16} className={isManualSyncing ? 'animate-spin text-primary' : 'text-slate-500'} />
                      Atualizar
                    </button>

                    <button
                      onClick={() => { setIsViewModalOpen(true); setIsMoreMenuOpen(false); }}
                      className="w-full flex items-center gap-3 p-3.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-all"
                    >
                      <Eye size={16} className="text-slate-500" />
                      Visualização
                    </button>

                    <button
                      onClick={() => { setIsSelectionMode(true); setIsMoreMenuOpen(false); }}
                      className="w-full flex items-center gap-3 p-3.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-all"
                    >
                      <GripVertical size={16} className="text-slate-500" />
                      Mover / Selecionar
                    </button>

                    <label className="w-full flex items-center gap-3 p-3.5 text-xs font-semibold text-surface bg-ink hover:bg-primary/90 rounded-2xl cursor-pointer transition-all mt-1">
                      <Plus size={16} />
                      <span>Adicionar Livro</span>
                      <input type="file" accept=".pdf,.html,.htm,.epub" className="hidden" onChange={(e) => { e.preventDefault(); handleFileUpload(e); setIsMoreMenuOpen(false); }} />
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Visualização Modal */}
        {isViewModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm" onClick={() => setIsViewModalOpen(false)}>
            <div className="bg-surface w-full max-w-sm rounded-[28px] p-6 border border-black/[0.06] dark:border-white/[0.06] space-y-6 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-black/[0.06] dark:border-white/[0.06] pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-primary/10 text-primary rounded-2xl"><Eye size={20} /></div>
                  <h3 className="text-base font-semibold dark:text-ink"><T k="ui.home.viewFilters" /></h3>
                </div>
                <button onClick={() => setIsViewModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-full"><X size={20} /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 mb-2"><T k="ui.home.displayMode" /></p>
                  <div className="grid grid-cols-2 gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl">
                    <button
                      onClick={() => { setViewMode('grid'); localStorage.setItem('viewMode', 'grid'); }}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-xs transition-all ${viewMode === 'grid' ? 'bg-surface dark:bg-slate-700 text-primary' : 'text-slate-500'}`}
                    >
                      <LayoutGrid size={16} /> Grade
                    </button>
                    <button
                      onClick={() => { setViewMode('list'); localStorage.setItem('viewMode', 'list'); }}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-xs transition-all ${viewMode === 'list' ? 'bg-surface dark:bg-slate-700 text-primary' : 'text-slate-500'}`}
                    >
                      <ListIcon size={16} /> Lista
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 mb-2"><T k="ui.home.shelfFilters" /></p>
                  <div className="space-y-1 max-h-60 overflow-y-auto custom-scrollbar">
                    {[
                      { id: 'recent', label: t('library.filter.recent', 'Adicionados recentes'), icon: <Plus size={14} /> },
                      { id: 'last_read', label: t('library.filter.lastRead', 'Acessado por último'), icon: <RefreshCw size={14} /> },
                      { id: 'favorites', label: t('library.filter.favorites', 'Favoritos'), icon: <Heart size={14} /> },
                      { id: 'reading', label: t('library.filter.reading', 'Lendo'), icon: <BookOpen size={14} /> },
                      { id: 'unread', label: t('library.filter.unread', 'Não lidos'), icon: <Bookmark size={14} /> },
                      { id: 'completed', label: t('library.filter.completed', 'Leitura completa'), icon: <ShieldCheck size={14} /> }
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => { setShelfFilter(opt.id as any); setCurrentPage(1); }}
                        className={`w-full flex items-center gap-3 p-3 text-xs font-bold rounded-2xl transition-all ${shelfFilter === opt.id ? 'bg-primary/10 text-primary' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                      >
                        {opt.icon}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-[13px] font-medium w-full h-12 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-full hover:bg-slate-200 transition-all"
              >
                Concluir
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* MINHA ESTANTE */}
      <div className="space-y-12 mt-12">
        <WatchdogGuard isLoading={dataLoading} onRetry={() => refreshBooks(true)}>
          {filteredAndSortedBooks.length === 0 ? (
            dataLoading ? (
            <div data-tour="library-shelf" className={viewMode === 'grid' ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6" : "space-y-4"}>
              {[1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : (
            <div data-tour="library-shelf" className="rounded-[32px] bg-surface border border-black/[0.06] dark:border-white/[0.06] px-8 py-16 sm:p-20 text-center flex flex-col items-center justify-center space-y-5">
              <div className="w-20 h-20 rounded-full bg-tint-peach text-ink flex items-center justify-center"><AnimatedIcon motion="flip" active><BookIcon size={30} strokeWidth={1.5} /></AnimatedIcon></div>
              <h3 className="font-display text-2xl sm:text-3xl font-light tracking-[-0.04em] text-ink">
                {shelfFilter === 'favorites' ? 'Nenhum favorito encontrado' : 
                 shelfFilter === 'reading' ? 'Nenhum livro em leitura no momento' :
                 shelfFilter === 'completed' ? 'Nenhum livro concluído ainda' :
                 shelfFilter === 'unread' ? 'Todos os seus livros já foram abertos!' :
                 'Sua estante está vazia'}
              </h3>
              {filteredAndSortedBooks.length === 0 && shelfFilter === 'recent' && (
                <p className="text-muted text-sm max-w-sm leading-relaxed">
                  {t('library.empty.message', 'Não se preocupe! Você pode importar livros gratuitamente da')}
                  {' '}
                  <span 
                    onClick={() => navigate('/libritui-library')}
                    className="cursor-pointer font-bold hover:underline transition-all"
                    style={{ color: accentColor }}
                  >
                    {t('library.empty.link', 'Biblioteca Online, clique aqui!')}
                  </span>
                </p>
              )}
            </div>
          )
        ) : (
          <div className={viewMode === 'grid' ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6" : "space-y-4"}>
            {currentBooks.map((book, index) => (
              <div 
                id={index === 0 ? 'tour-book-card' : undefined}
                key={book.id} 
              >
                {viewMode === 'grid'
                  ? <BookCardGrid
                    book={book}
                    isSelectionMode={isSelectionMode}
                    isSelected={selectedIds.has(book.id)}
                    onSelect={() => handleToggleSelection(book.id)}
                    onDelete={(id) => { setActiveBookId(id); setModalType('delete'); }}
                    onToggleFavorite={handleToggleFavorite}
                    onToggleCompleted={handleToggleCompleted}
                    onRename={(id) => { setActiveBookId(id); setModalType('rename'); }}
                    onChangeCover={(id) => { setActiveBookId(id); setModalType('cover'); }}
                    onOpen={() => handleOpenBook(book)}
                    onMove={(delta) => handleMoveBook(book.id, delta)}
                    isOffline={offlineBookIds.has(book.id)}
                    isDownloading={downloadingBookId === book.id}
                    onDownload={() => confirmDownloadOffline(book.id)}
                  />
                  : <BookCardList
                    book={book}
                    isSelectionMode={isSelectionMode}
                    isSelected={selectedIds.has(book.id)}
                    onSelect={() => handleToggleSelection(book.id)}
                    onDelete={(id) => { setActiveBookId(id); setModalType('delete'); }}
                    onToggleFavorite={handleToggleFavorite}
                    onToggleCompleted={handleToggleCompleted}
                    onRename={(id) => { setActiveBookId(id); setModalType('rename'); }}
                    onOpen={() => handleOpenBook(book)}
                    onMove={(delta) => handleMoveBook(book.id, delta)}
                    isOffline={offlineBookIds.has(book.id)}
                    isDownloading={downloadingBookId === book.id}
                    onDownload={() => confirmDownloadOffline(book.id)}
                  />
                }
              </div>
            ))}
          </div>
        )}
        </WatchdogGuard>
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-6 pt-12 border-t border-black/[0.06] dark:border-white/[0.06]">
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                disabled={currentPage === 1} 
                className="w-12 h-12 flex items-center justify-center bg-primary text-slate-900 rounded-2xl disabled:opacity-20 hover:scale-105 transition-all"
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button 
                    key={p} 
                    onClick={() => setCurrentPage(p)} 
                    className={`w-12 h-12 rounded-2xl font-semibold text-sm transition-all active:scale-95 flex items-center justify-center ${
                      currentPage === p 
                        ? 'bg-ink text-surface scale-110' 
                        : 'bg-surface dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-black/[0.06] dark:border-slate-700 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                disabled={currentPage === totalPages} 
                className="w-12 h-12 flex items-center justify-center bg-primary text-slate-900 rounded-2xl disabled:opacity-20 hover:scale-105 transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedBookForModal && <BookDetailsModal book={{
        ...selectedBookForModal,
        cover: selectedBookForModal.coverUrl,
        file: selectedBookForModal.fileUrl,
        description: selectedBookForModal.description,
        reading_level: selectedBookForModal.reading_level,
        subjects: typeof selectedBookForModal.subjects === 'string' 
          ? JSON.parse(selectedBookForModal.subjects) 
          : selectedBookForModal.subjects,
        format: (() => {
          const b = selectedBookForModal;
          if (b.format === 'html') return 'html';
          if (b.format === 'pdf') return 'pdf';
          if (localStorage.getItem(`mime_${b.id}`) === 'text/html') return 'html';
          if (b.category?.toLowerCase().includes('html')) return 'html';
          if (b.fileUrl?.match(/\.html?$/i)) return 'html';
          return 'pdf';
        })()
      }} onClose={() => setSelectedBookForModal(null)} onImport={(b) => { 
        if (tourService.isActive() && tourService.getPhase() === 'LIBRARY') {
          tourService.setPhase('READER');
        }
        if (isShadowingView) {
          navigate(`/reader/${b.id || b.custom_id}`, {
            state: { shadowing: true, from: '/shadowing', importedBook: b }
          });
        } else {
          navigate(`/reader/${b.id || b.custom_id}`); 
        }
        setSelectedBookForModal(null); 
      }} onPreview={() => { setShowNotesForBook(selectedBookForModal); setSelectedBookForModal(null); }} isImporting={false} languageFlags={LANGUAGE_FLAGS} renderCover={() => <BookCover book={selectedBookForModal} />} primaryLabel={t('library.openBook')} secondaryLabel={t('library.notesLabel')} primaryIcon={<BookOpen size={20} />} secondaryIcon={<MessageSquare size={20} />} />}
      {showNotesForBook && <NotesListModal book={showNotesForBook} onClose={() => setShowNotesForBook(null)} />}
      <ConfirmationModal isOpen={modalType === 'download_confirm'} title={t('home.downloadConfirmTitle', 'Deseja baixar este livro?')} message={t('home.downloadConfirmDesc', 'Dessa forma você pode continuar seu estudo em qualquer lugar, mesmo sem internet!')} confirmLabel={t('home.downloadBtn', 'Baixar')} cancelLabel={t('home.cancelBtn', 'Cancelar')} onConfirm={executeDownloadOffline} onCancel={() => setModalType('none')} />
      <ConfirmationModal isOpen={modalType === 'delete'} title={t('home.deleteBtn', 'Excluir')} message={`Tem certeza que quer apagar "${books.find(b => b.id === activeBookId)?.title}"? Esta ação não pode ser desfeita.`} confirmLabel={t('home.deleteBtn', 'Excluir')} isDanger onConfirm={executeDelete} onCancel={() => setModalType('none')} />
      <ConfirmationModal isOpen={modalType === 'bulk_delete'} title={t('home.deleteBtn', 'Excluir Selecionados?')} message={`Você está prestes a apagar ${selectedIds.size} livros permanentemente. Esta ação não pode ser desfeita.`} confirmLabel={t('home.deleteBtn', 'Excluir Tudo')} isDanger onConfirm={handleBatchDelete} onCancel={() => setModalType('none')} />
      <ConfirmationModal 
        isOpen={modalType === 'delete_success'} 
        title={i18nInstance.t('ui.home.booksDeleted')} 
        message="Os livros selecionados foram removidos com sucesso da sua estante." 
        confirmLabel="Confirmar" 
        hideCancel={true}
        onConfirm={async () => {
          setModalType('none');
          await refreshBooks();
        }} 
        onCancel={() => setModalType('none')} 
      />
      <InputModal isOpen={modalType === 'rename'} title={t('home.renameBook')} placeholder="Novo nome..." initialValue={books.find(b => b.id === activeBookId)?.title || ''} onSave={executeRename} onCancel={() => setModalType('none')} />
      <InputModal isOpen={modalType === 'cover'} title={t('home.changeCover')} placeholder="URL da imagem..." initialValue={books.find(b => b.id === activeBookId)?.coverUrl || ''} onSave={executeChangeCover} onCancel={() => setModalType('none')} />

      <ConfirmationModal
        isOpen={modalType === 'limit_reached'}
        title="Limite Atingido"
        message="Como usuário Free, você pode ter até 3 livros na sua estante. Torne-se PRO agora para importar livros ilimitados e apoiar o projeto!"
        confirmLabel={t('home.seeProPlans')}
        onConfirm={() => { setModalType('upgrade'); }}
        onCancel={() => setModalType('none')}
      />

      <ConfirmationModal
        isOpen={modalType === 'upgrade'}
        title="🚀 Explore o LibriTui PRO"
        message={t('home.proDesc')}
        confirmLabel={t('home.yesWantPro')}
        onConfirm={() => { window.open('https://libritui.com/pro', '_blank'); setModalType('none'); }}
        onCancel={() => setModalType('none')}
      />
    </div>
  );
};

export const BookCardGrid: React.FC<{
  book: Book,
  isSelectionMode: boolean,
  isSelected: boolean,
  onSelect: () => void,
  onDelete: (id: string) => void,
  onToggleFavorite: (id: string) => void,
  onToggleCompleted?: (id: string) => void,
  onRename: (id: string) => void,
  onChangeCover: (id: string) => void,
  onOpen: () => void,
  onMove: (delta: number) => void,
  isOffline: boolean,
  isDownloading: boolean,
  onDownload: () => void
}> = ({ book, isSelectionMode, isSelected, onSelect, onDelete, onToggleFavorite, onToggleCompleted = () => {}, onRename, onChangeCover, onOpen, onMove, isOffline, isDownloading, onDownload }) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const { user } = useAuth();
  const progress = useMemo(() => {
    if (book.progress !== undefined) return book.progress;
    if (user && !user.isGuest) return book.totalPages > 0 ? Math.min(100, Math.round(((book.lastPage || 1) / book.totalPages) * 100)) : 0;
    const data = localStorage.getItem(`progresso_${book.id}`);
    return data ? (JSON.parse(data) as any).percent : (book.totalPages > 0 ? Math.min(100, Math.round(((book.lastPage || 1) / book.totalPages) * 100)) : 0);
  }, [book, user]);

  const isCompleted = useMemo(() => {
    return book.status === 'completed' || !!book.isCompleted;
  }, [book]);

  return (
    <div
      className={`group relative transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isSelectionMode ? 'scale-[0.98]' : 'hover:-translate-y-1.5'} ${showMenu ? 'z-[100]' : 'z-10'}`}
    >
      <div
        onClick={isSelectionMode ? onSelect : onOpen}
        className={`relative aspect-[2/3] rounded-[22px] overflow-hidden bg-tint-stone cursor-pointer transition-shadow duration-500 group-hover:shadow-[0_24px_50px_-28px_rgba(0,0,0,0.55)] ${isSelected ? 'ring-4 ring-primary ring-inset' : ''}`}
      >
        <div className={`w-full h-full transition-all duration-300 ${isSelected ? 'bg-black/80 brightness-[0.3]' : isSelectionMode ? 'brightness-75' : ''}`}>
          <BookCover book={book} />
        </div>

        {book.category === 'clube_do_livro' && (
          <div className={`absolute top-3 left-3 z-20 flex items-center gap-1 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm text-primary font-medium text-[9px] px-2.5 py-1.5 rounded-full border border-primary/20 transition-opacity ${isSelectionMode ? 'opacity-30' : 'opacity-100'}`}>
            🤝 Clube do Livro
          </div>
        )}

        {isSelectionMode && (
          <div className="absolute inset-0 flex items-center justify-center z-30">
            <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-md p-2 rounded-full border border-white/20 animate-in zoom-in-90">
              <button
                onClick={(e) => { e.stopPropagation(); onMove(-1); }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all hover:scale-110 active:scale-90"
                title={t('home.moveLeft', 'Mover para a esquerda')}
              >
                <ChevronLeft size={20} />
              </button>

              <div
                onClick={onSelect}
                className={`w-12 h-12 rounded-full border flex items-center justify-center cursor-pointer transition-all ${isSelected ? 'bg-ink border-primary text-surface scale-110' : 'bg-white/20 border-white/40 text-white'}`}
              >
                <Plus size={24} className={`transition-transform duration-300 ${isSelected ? 'rotate-45' : ''}`} />
              </div>

              <button
                onClick={(e) => { e.stopPropagation(); onMove(1); }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all hover:scale-110 active:scale-90"
                title={t('home.moveRight', 'Mover para a direita')}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Título, autor e progresso abaixo da capa (design 2.0) */}
      <div onClick={isSelectionMode ? onSelect : onOpen} className={`mt-3 px-1 cursor-pointer transition-opacity ${isSelectionMode ? 'opacity-40' : ''}`}>
        <h4 className="text-[13px] font-medium text-ink leading-snug line-clamp-2">{book.title}</h4>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 h-1 rounded-full bg-ink/[0.07] overflow-hidden"><div className="h-full bg-ink rounded-full transition-all duration-700" style={{ width: `${progress}%` }} /></div>
          <span className="text-[10px] font-mono tabular-nums text-muted">{isCompleted ? 'lido' : `${progress}%`}</span>
        </div>
      </div>

      {!isSelectionMode && (
        <div className="absolute top-2.5 right-2.5 z-30 flex items-center gap-1.5">
          {/* Lido: marca discreta à esquerda da estrela */}
          {isCompleted && (
            <div
              className="w-8 h-8 backdrop-blur-md rounded-full bg-black/35 text-[#C2C8A0] flex items-center justify-center pointer-events-none"
              title={i18nInstance.t('ui.common.done')}
            >
              <Check size={14} strokeWidth={2.5} />
            </div>
          )}

          {/* Favorito */}
          {book.isFavorite !== undefined && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleFavorite(book.id); }}
              className={`w-8 h-8 backdrop-blur-md rounded-full flex items-center justify-center transition-all ${book.isFavorite ? 'bg-black/35 text-[#F2A86F]' : 'bg-black/35 text-white opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
              title={book.isFavorite ? t('library.favorite_remove', 'Remover dos favoritos') : t('library.favorite_add', 'Adicionar aos favoritos')}
            >
              <AnimatedIcon motion="spin" active={!!book.isFavorite}><Heart size={14} strokeWidth={1.75} fill={book.isFavorite ? "currentColor" : "none"} /></AnimatedIcon>
            </button>
          )}

          {/* Menu */}
          <div className="relative z-50">
            <button onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }} className="w-8 h-8 bg-black/35 backdrop-blur-md rounded-full text-white hover:bg-black/55 flex items-center justify-center transition-all" aria-label={i18nInstance.t('ui.common.options')}><MoreVertical size={14} /></button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-[90] sm:hidden" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
                <div className="fixed inset-0 z-[90] hidden sm:block" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
                <div className="fixed bottom-0 left-0 right-0 p-6 pb-8 bg-surface rounded-t-[2rem] shadow-[0_-10px_40px_rgba(0,0,0,0.15)] sm: sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-48 sm:rounded-2xl border border-black/[0.06] dark:border-white/[0.06] sm:p-2 z-[100] animate-in slide-in-from-bottom-full sm:slide-in-from-top-2 duration-300" onClick={e => e.stopPropagation()}>
                <button onClick={() => { onToggleCompleted(book.id); setShowMenu(false); }} className="w-full flex items-center gap-3 p-3 text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-primary rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isCompleted ? 'bg-ok border-ok text-surface' : 'border-slate-400 dark:border-slate-600'}`}>
                    {isCompleted && <Check size={12} strokeWidth={3} />}
                  </div>
                  <span>Lido</span>
                </button>
                <button onClick={() => { onDownload(); setShowMenu(false); }} disabled={isDownloading} className="w-full flex items-center gap-3 p-3 text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-primary rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  <Download size={16} /> {isOffline ? t('home.removeDownloadBtn', 'Excluir dos baixados') : t('home.downloadBtn', 'Baixar para ler offline')}
                </button>
                <button onClick={() => { onRename(book.id); setShowMenu(false); }} className="w-full flex items-center gap-3 p-3 text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-primary rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Edit3 size={16} /> {t('home.renameBtn', 'Renomear')}</button>
                <button onClick={() => { setShowMenu(false); setShowRating(true); }} className="w-full flex items-center gap-3 p-3 text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-primary rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Star size={16} /> {t('ratings.rate', 'Avaliar')}</button>
                <button onClick={() => { onDelete(book.id); setShowMenu(false); }} className="w-full flex items-center gap-3 p-3 text-sm font-bold text-bad rounded-xl hover:bg-tint-rose transition-colors"><Trash2 size={16} /> {t('home.deleteBtn', 'Excluir')}</button>
              </div>
              </>
            )}
          </div>
        </div>
      )}
      <RatingModal
        open={showRating}
        onClose={() => setShowRating(false)}
        contentId={String(book.id)}
        contentType="book"
        title={book.title}
        author={book.author}
        language={book.language}
      />
    </div>
  );
};

const BookCardList: React.FC<{
  book: Book,
  isSelectionMode: boolean,
  isSelected: boolean,
  onSelect: () => void,
  onDelete: (id: string) => void,
  onToggleFavorite: (id: string) => void,
  onToggleCompleted: (id: string) => void,
  onRename: (id: string) => void,
  onOpen: () => void,
  onMove: (delta: number) => void,
  isOffline: boolean,
  isDownloading: boolean,
  onDownload: () => void
}> = ({ book, isSelectionMode, isSelected, onSelect, onDelete, onToggleFavorite, onToggleCompleted, onRename, onOpen, onMove, isOffline, isDownloading, onDownload }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  const progress = useMemo(() => {
    if (!user) return book.progress || 0;
    const data = localStorage.getItem(`libritui_book_progress_${user.id}_${book.id}`);
    return data ? (JSON.parse(data) as any).percent : (book.totalPages > 0 ? Math.min(100, Math.round(((book.lastPage || 1) / book.totalPages) * 100)) : 0);
  }, [book, user]);

  const isCompleted = useMemo(() => {
    return book.status === 'completed' || !!book.isCompleted;
  }, [book]);

  return (
    <div
      className={`flex items-center gap-8 p-6 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[32px] group relative overflow-hidden transition-all ${isSelected ? 'ring-4 ring-primary ring-inset bg-primary/5' : ''}`}
    >
      <div onClick={isSelectionMode ? onSelect : onOpen} className="flex items-center gap-8 flex-1 cursor-pointer">
        <div className="w-20 h-28 rounded-2xl overflow-hidden relative flex-shrink-0">
          <div className={`w-full h-full transition-all duration-300 ${isSelected ? 'bg-black/80 brightness-[0.3]' : isSelectionMode ? 'brightness-75' : ''}`}>
            <BookCover book={book} isSmall />
          </div>
          {isSelectionMode && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 z-30 bg-black/40 backdrop-blur-[2px]">
              <div
                onClick={onSelect}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all cursor-pointer ${isSelected ? 'bg-ink border-primary text-surface scale-110' : 'bg-white/20 border-white/40 text-white'}`}
              >
                <Plus size={20} className={`transition-transform duration-300 ${isSelected ? 'rotate-45' : ''}`} />
              </div>
            </div>
          )}
          <div className={`absolute bottom-0 left-0 right-0 h-1 bg-black/30 transition-opacity ${isSelectionMode ? 'opacity-30' : 'opacity-100'}`}><div className="h-full bg-ink" style={{ width: `${progress}%` }} /></div>
        </div>
        <div className={`flex-1 min-w-0 transition-opacity ${isSelectionMode && !isSelected ? 'opacity-50' : isSelectionMode && isSelected ? 'opacity-30' : 'opacity-100'}`}>
          <h3 className="font-semibold text-xl text-ink truncate group-hover:text-primary transition-colors">{book.title}</h3>
          <div className="flex flex-col gap-2 mt-2">
            <div className="flex items-center justify-between text-[11px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400">
              <span>{progress}% Concluído</span>
              <span className="truncate ml-4">{book.author || "Autor Desconhecido"}</span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
               <div className="h-full bg-ink transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>
      {!isSelectionMode ? (
        <div className="flex items-center gap-2">
          {isCompleted && (
            <div className="p-3.5 rounded-xl bg-black/40 text-ok opacity-50 flex items-center justify-center" title="Lido">
              <Check size={20} strokeWidth={3} />
            </div>
          )}
          <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(book.id); }} className={`p-4 rounded-xl transition-all opacity-50 hover:opacity-100 ${book.isFavorite ? 'text-bad bg-tint-rose dark:bg-tint-rose/20' : 'text-slate-300 hover:text-bad hover:bg-slate-50 dark:hover:bg-slate-800'}`} title={book.isFavorite ? t('library.favorite_remove', 'Remover dos favoritos') : t('library.favorite_add', 'Adicionar aos favoritos')}><Heart size={24} fill={book.isFavorite ? "currentColor" : "none"} /></button>
          <button onClick={(e) => { e.stopPropagation(); setShowRating(true); }} className="p-4 text-slate-300 hover:text-warn hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all" title={t('ratings.rate', 'Avaliar')}><Star size={24} /></button>
          <button onClick={(e) => { e.stopPropagation(); onToggleCompleted(book.id); }} className="p-4 text-slate-300 hover:text-ok hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all" title={isCompleted ? 'Marcar como não lido' : 'Marcar como lido'}>
            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isCompleted ? 'bg-ok border-ok text-surface' : 'border-slate-400 dark:border-slate-600'}`}>
              {isCompleted && <Check size={14} strokeWidth={3} />}
            </div>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onRename(book.id); }} className="p-4 text-slate-300 hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all"><Edit3 size={24} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(book.id); }} className="p-4 text-slate-300 hover:text-bad hover:bg-tint-rose rounded-xl transition-all"><Trash2 size={24} /></button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 animate-in slide-in-from-right duration-300">
          <button
            onClick={(e) => { e.stopPropagation(); onMove(-1); }}
            className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-primary rounded-xl transition-all hover:scale-110 active:scale-95"
            title={t('home.moveUp', 'Mover para cima')}
          >
            <ChevronUp size={20} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMove(1); }}
            className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-primary rounded-xl transition-all hover:scale-110 active:scale-95"
            title={t('home.moveDown', 'Mover para baixo')}
          >
            <ChevronDown size={20} />
          </button>
        </div>
      )}
      <RatingModal
        open={showRating}
        onClose={() => setShowRating(false)}
        contentId={String(book.id)}
        contentType="book"
        title={book.title}
        author={book.author}
        language={book.language}
      />
    </div>
  );
};

export default HomePage;
